import {
  isPlaybookCriterionNode,
  isPlaybookSectionNode
} from "../entities/playbook.js";
function removeSection(template, sectionId) {
  let removedSection;
  const removeFromNodes = (nodes) => {
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        if (node.id === sectionId) {
          removedSection = node;
          return null;
        }
        return {
          ...node,
          nodes: removeFromNodes(node.nodes)
        };
      }
      return node;
    }).filter((node) => node !== null);
  };
  const newTemplate = {
    ...template,
    nodes: removeFromNodes(template.nodes)
  };
  return [newTemplate, removedSection];
}
function removeCriteria(template, criteriaId) {
  let removedCriteria;
  const removeFromNodes = (nodes) => {
    return nodes.map((node) => {
      if (isPlaybookCriterionNode(node)) {
        if (node.id === criteriaId) {
          removedCriteria = node;
          return null;
        }
        return node;
      } else if (isPlaybookSectionNode(node)) {
        return {
          ...node,
          nodes: removeFromNodes(node.nodes)
        };
      }
      return node;
    }).filter((node) => node !== null);
  };
  const newTemplate = {
    ...template,
    nodes: removeFromNodes(template.nodes)
  };
  return [newTemplate, removedCriteria];
}
function addSection(template, newSection, parentSectionId) {
  if (parentSectionId) {
    const updateNodes = (nodes) => {
      return nodes.map((node) => {
        if (isPlaybookSectionNode(node)) {
          if (node.id === parentSectionId) {
            return {
              ...node,
              nodes: [...node.nodes, newSection]
            };
          }
          return { ...node, nodes: updateNodes(node.nodes) };
        }
        return node;
      });
    };
    return {
      ...template,
      nodes: updateNodes(template.nodes)
    };
  }
  return {
    ...template,
    nodes: [...template.nodes, newSection]
  };
}
function addCriteria(template, newCriteria, sectionId) {
  const updateNodes = (nodes) => {
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        if (node.id === sectionId) {
          return {
            ...node,
            nodes: [...node.nodes, newCriteria]
          };
        }
        return { ...node, nodes: updateNodes(node.nodes) };
      }
      return node;
    });
  };
  return {
    ...template,
    nodes: updateNodes(template.nodes)
  };
}
function updateSection(template, updatedSection) {
  const updateNodes = (nodes) => {
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        if (node.id === updatedSection.id) {
          return updatedSection;
        }
        return { ...node, nodes: updateNodes(node.nodes) };
      }
      return node;
    });
  };
  return {
    ...template,
    nodes: updateNodes(template.nodes)
  };
}
function updateCriteria(template, updatedCriteria) {
  const updateNodes = (nodes) => {
    return nodes.map((node) => {
      if (isPlaybookCriterionNode(node)) {
        return node.id === updatedCriteria.id ? updatedCriteria : node;
      } else if (isPlaybookSectionNode(node)) {
        return { ...node, nodes: updateNodes(node.nodes) };
      }
      return node;
    });
  };
  return {
    ...template,
    nodes: updateNodes(template.nodes)
  };
}
function findSection(template, sectionId) {
  const searchNodes = (nodes) => {
    for (const node of nodes) {
      if (isPlaybookSectionNode(node)) {
        if (node.id === sectionId) {
          return node;
        }
        const found = searchNodes(node.nodes);
        if (found) {
          return found;
        }
      }
    }
    return void 0;
  };
  return searchNodes(template.nodes);
}
function findCriteria(template, criteriaId) {
  const searchNodes = (nodes) => {
    for (const node of nodes) {
      if (isPlaybookCriterionNode(node)) {
        if (node.id === criteriaId) {
          return node;
        }
      } else if (isPlaybookSectionNode(node)) {
        const found = searchNodes(node.nodes);
        if (found) {
          return found;
        }
      }
    }
    return void 0;
  };
  return searchNodes(template.nodes);
}
function moveSectionBefore(template, sectionToMoveId, targetSectionId) {
  if (sectionToMoveId === targetSectionId) {
    return template;
  }
  const [templateWithoutSection, sectionToMove] = removeSection(
    template,
    sectionToMoveId
  );
  if (!sectionToMove) {
    return template;
  }
  const insertIntoNodes = (nodes) => {
    const targetIndex = nodes.findIndex(
      (node) => isPlaybookSectionNode(node) && node.id === targetSectionId
    );
    if (targetIndex !== -1) {
      const newNodes = [...nodes];
      newNodes.splice(targetIndex, 0, sectionToMove);
      return newNodes;
    }
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        return {
          ...node,
          nodes: insertIntoNodes(node.nodes)
        };
      }
      return node;
    });
  };
  return {
    ...templateWithoutSection,
    nodes: insertIntoNodes(templateWithoutSection.nodes)
  };
}
function moveSectionAfter(template, sectionToMoveId, targetSectionId) {
  if (sectionToMoveId === targetSectionId) {
    return template;
  }
  const [templateWithoutSection, sectionToMove] = removeSection(
    template,
    sectionToMoveId
  );
  if (!sectionToMove) {
    return template;
  }
  const insertIntoNodes = (nodes) => {
    const targetIndex = nodes.findIndex(
      (node) => isPlaybookSectionNode(node) && node.id === targetSectionId
    );
    if (targetIndex !== -1) {
      const newNodes = [...nodes];
      newNodes.splice(targetIndex + 1, 0, sectionToMove);
      return newNodes;
    }
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        return {
          ...node,
          nodes: insertIntoNodes(node.nodes)
        };
      }
      return node;
    });
  };
  return {
    ...templateWithoutSection,
    nodes: insertIntoNodes(templateWithoutSection.nodes)
  };
}
function moveCriteriaBefore(template, criteriaToMoveId, targetCriteriaId) {
  if (criteriaToMoveId === targetCriteriaId) {
    return template;
  }
  const [templateWithoutCriteria, criteriaToMove] = removeCriteria(
    template,
    criteriaToMoveId
  );
  if (!criteriaToMove) {
    return template;
  }
  const insertIntoNodes = (nodes) => {
    const targetIndex = nodes.findIndex(
      (node) => isPlaybookCriterionNode(node) && node.id === targetCriteriaId
    );
    if (targetIndex !== -1) {
      const newNodes = [...nodes];
      newNodes.splice(targetIndex, 0, criteriaToMove);
      return newNodes;
    }
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        return {
          ...node,
          nodes: insertIntoNodes(node.nodes)
        };
      }
      return node;
    });
  };
  return {
    ...templateWithoutCriteria,
    nodes: insertIntoNodes(templateWithoutCriteria.nodes)
  };
}
function moveCriteriaAfter(template, criteriaToMoveId, targetCriteriaId) {
  if (criteriaToMoveId === targetCriteriaId) {
    return template;
  }
  const [templateWithoutCriteria, criteriaToMove] = removeCriteria(
    template,
    criteriaToMoveId
  );
  if (!criteriaToMove) {
    return template;
  }
  const insertIntoNodes = (nodes) => {
    const targetIndex = nodes.findIndex(
      (node) => isPlaybookCriterionNode(node) && node.id === targetCriteriaId
    );
    if (targetIndex !== -1) {
      const newNodes = [...nodes];
      newNodes.splice(targetIndex + 1, 0, criteriaToMove);
      return newNodes;
    }
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        return {
          ...node,
          nodes: insertIntoNodes(node.nodes)
        };
      }
      return node;
    });
  };
  return {
    ...templateWithoutCriteria,
    nodes: insertIntoNodes(templateWithoutCriteria.nodes)
  };
}
function moveCriteriaToSection(template, criteriaToMoveId, targetSectionId) {
  const [templateWithoutCriteria, criteriaToMove] = removeCriteria(
    template,
    criteriaToMoveId
  );
  if (!criteriaToMove) {
    return template;
  }
  const insertIntoNodes = (nodes) => {
    return nodes.map((node) => {
      if (isPlaybookSectionNode(node)) {
        if (node.id === targetSectionId) {
          return {
            ...node,
            nodes: [...node.nodes, criteriaToMove]
          };
        }
        return {
          ...node,
          nodes: insertIntoNodes(node.nodes)
        };
      }
      return node;
    });
  };
  return {
    ...templateWithoutCriteria,
    nodes: insertIntoNodes(templateWithoutCriteria.nodes)
  };
}
function applyPlaybookOperation(tree, operation) {
  switch (operation.type) {
    case "remove-section": {
      const [newTree, removed] = removeSection(tree, operation.id);
      return removed ? newTree : null;
    }
    case "remove-criteria": {
      const [newTree, removed] = removeCriteria(tree, operation.id);
      return removed ? newTree : null;
    }
  }
}
export {
  addCriteria,
  addSection,
  applyPlaybookOperation,
  findCriteria,
  findSection,
  moveCriteriaAfter,
  moveCriteriaBefore,
  moveCriteriaToSection,
  moveSectionAfter,
  moveSectionBefore,
  removeCriteria,
  removeSection,
  updateCriteria,
  updateSection
};
//# sourceMappingURL=playbookTreeOperations.js.map
